import { useState } from "react";

import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { TagSelect } from "@/components/TagSelect/TagSelect";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

type EventParticipantGuestsModalProps = ModalBaseProps & {
  participantName: string;
  initialGuestCount: number;
  maxGuests: number;
  isLoading: boolean;
  submitLabel: string;
  onConfirm: (guestCount: number) => void;
};

export function EventParticipantGuestsModal({
  participantName,
  initialGuestCount,
  maxGuests,
  isLoading,
  submitLabel,
  isOpened,
  onOpenChange,
  onConfirm,
}: EventParticipantGuestsModalProps): React.ReactNode {
  const { t } = useTranslation();
  const [guestCount, setGuestCount] = useState(Math.min(initialGuestCount, maxGuests));
  const options = Array.from({ length: maxGuests + 1 }, (_, count) => ({
    label:
      count === 0
        ? t("page.event.participants.guests.none")
        : t("page.event.details.participants.guest-count", { count }),
    value: String(count),
  }));

  return (
    <Modal.Root
      title={t("page.event.participants.guests.title")}
      description={participantName}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      size="sm"
      isNested
      data-testid="event-participant-guests-modal"
    >
      <Modal.Body>
        <Stack gap="sm">
          <label id="participant-guests-label" className="text-caption-emphasis text-text-primary">
            {t("page.event.participants.guests.label")}
          </label>
          <TagSelect
            items={options}
            values={[String(guestCount)]}
            onChange={(value) => {
              if (value[0] !== undefined) {
                setGuestCount(Number(value[0]));
              }
            }}
            aria-labelledby="participant-guests-label"
          />
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Button onClick={() => onConfirm(guestCount)} isLoading={isLoading} data-testid="save-participant-guests">
          {submitLabel}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
